// frontend/src/components/Dashboard/TraficoCard.js
// Recuadro de tráfico del sitio en el dashboard admin. Lee GET
// /api/trafico/resumen (ver backend/src/routes/trafico.js).

import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from 'chart.js';
import { Activity, Loader2, Monitor, Smartphone, Tablet, HelpCircle, MapPin } from 'lucide-react';
import './TraficoCard.css';

// Leaflet pesa ~45 KB: se descarga solo al abrir el dashboard admin, no en
// el landing ni en el resto de la app
const TraficoMapa = lazy(() => import('./TraficoMapa'));

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend);

const RANGOS = [7, 30, 90];

const NOMBRES_RUTA = {
  '/': 'Landing (inicio)',
  '/login': 'Inicio de sesión',
  '/registro': 'Registro de empresa',
  '/solicitar-demo': 'Solicitar demostración'
};

const DISPOSITIVOS = {
  computadora: { texto: 'Computadora', icono: Monitor },
  celular: { texto: 'Celular', icono: Smartphone },
  tablet: { texto: 'Tablet', icono: Tablet },
  sin_dato: { texto: 'Sin dato', icono: HelpCircle }
};

// "León, Guanajuato" en México; fuera de México se agrega el país
let nombresPais = null;
try { nombresPais = new Intl.DisplayNames(['es'], { type: 'region' }); } catch { /* navegador sin Intl.DisplayNames */ }

const nombreUbicacion = ({ pais, estado, ciudad }) => {
  const partes = [ciudad, estado].filter(Boolean);
  if (pais !== 'MX') partes.push(nombresPais?.of(pais) || pais);
  return partes.join(', ') || pais;
};

// Paleta validada contra la superficie clara y la oscura del dashboard
const COLORES = {
  claro: { visitas: '#0B8A78', visitantes: '#6B46C1', texto: '#8A94A6', grid: '#E6EBEE' },
  oscuro: { visitas: '#2A9E8C', visitantes: '#9A7AE6', texto: '#7C928C', grid: '#324542' }
};

const formatoDia = (iso) => {
  const [anio, mes, dia] = iso.split('-').map(Number);
  return new Date(anio, mes - 1, dia).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
};

const TraficoCard = ({ modoOscuro }) => {
  const [dias, setDias] = useState(30);
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const response = await fetch(`/api/trafico/resumen?dias=${dias}`, { credentials: 'include' });
      if (!response.ok) throw new Error('No se pudo cargar el tráfico');
      setDatos(await response.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => { cargar(); }, [cargar]);

  const c = modoOscuro ? COLORES.oscuro : COLORES.claro;

  const variacion = datos && datos.visitas_periodo_anterior > 0
    ? Math.round(((datos.visitas_periodo - datos.visitas_periodo_anterior) / datos.visitas_periodo_anterior) * 100)
    : null;

  const chartData = datos && {
    labels: datos.serie.map(p => formatoDia(p.dia)),
    datasets: [
      {
        label: 'Visitas',
        data: datos.serie.map(p => p.visitas),
        borderColor: c.visitas,
        backgroundColor: c.visitas,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.3
      },
      {
        label: 'Visitantes únicos',
        data: datos.serie.map(p => p.visitantes),
        borderColor: c.visitantes,
        backgroundColor: c.visitantes,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        tension: 0.3
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: {
        position: 'top',
        align: 'end',
        labels: { color: c.texto, usePointStyle: true, pointStyle: 'line', boxWidth: 18 }
      },
      tooltip: { mode: 'index', intersect: false }
    },
    scales: {
      x: {
        grid: { display: false },
        border: { color: c.grid },
        ticks: { color: c.texto, maxTicksLimit: 8, maxRotation: 0 }
      },
      y: {
        beginAtZero: true,
        grid: { color: c.grid },
        border: { display: false },
        ticks: { color: c.texto, precision: 0 }
      }
    }
  };

  return (
    <section className="dash-page-card trafico-card">
      <div className="trafico-header">
        <h2><Activity size={18} /> Tráfico del sitio</h2>
        <div className="trafico-rangos" role="group" aria-label="Rango de días">
          {RANGOS.map(r => (
            <button
              key={r}
              type="button"
              className={r === dias ? 'activo' : ''}
              onClick={() => setDias(r)}
              aria-pressed={r === dias}
            >
              {r} días
            </button>
          ))}
        </div>
      </div>
      <p className="trafico-nota">Páginas públicas (landing, login y registro), solo de visitantes que aceptaron las cookies.</p>

      {cargando && !datos ? (
        <p className="text-muted trafico-estado"><Loader2 size={16} className="spin-icon" /> Cargando tráfico...</p>
      ) : error ? (
        <p className="text-muted trafico-estado">{error}. <button type="button" className="trafico-link" onClick={cargar}>Reintentar</button></p>
      ) : (
        <>
          <div className="trafico-kpis">
            <div className="trafico-kpi">
              <span>Visitas hoy</span>
              <strong>{datos.visitas_hoy.toLocaleString('es-MX')}</strong>
            </div>
            <div className="trafico-kpi">
              <span>Visitantes hoy</span>
              <strong>{datos.visitantes_hoy.toLocaleString('es-MX')}</strong>
            </div>
            <div className="trafico-kpi">
              <span>Visitas ({dias} días)</span>
              <strong>{datos.visitas_periodo.toLocaleString('es-MX')}</strong>
              {variacion !== null && (
                <small className={variacion >= 0 ? 'sube' : 'baja'}>
                  {variacion >= 0 ? '▲' : '▼'} {Math.abs(variacion)}% vs. {dias} días previos
                </small>
              )}
            </div>
            <div className="trafico-kpi">
              <span>Promedio visitantes / día</span>
              <strong>{datos.promedio_visitantes_dia.toLocaleString('es-MX')}</strong>
            </div>
          </div>

          <div className="trafico-cuerpo">
            <div className="trafico-grafica">
              <Line data={chartData} options={chartOptions} aria-label={`Visitas y visitantes únicos por día, últimos ${dias} días`} />
            </div>
            <div className="trafico-top">
              <h3>Páginas más visitadas</h3>
              {datos.top_rutas.length === 0 ? (
                <p className="text-muted">Aún no hay visitas en este periodo.</p>
              ) : (
                <ol>
                  {datos.top_rutas.map(r => (
                    <li key={r.ruta}>
                      <span title={r.ruta}>{NOMBRES_RUTA[r.ruta] || r.ruta}</span>
                      <strong>{r.visitas.toLocaleString('es-MX')}</strong>
                    </li>
                  ))}
                </ol>
              )}

              <h3 className="trafico-subtitulo">Dispositivos</h3>
              {(datos.por_dispositivo || []).length === 0 ? (
                <p className="text-muted">Sin datos en este periodo.</p>
              ) : (
                <ul className="trafico-dispositivos">
                  {datos.por_dispositivo.map(d => {
                    const info = DISPOSITIVOS[d.dispositivo] || { texto: d.dispositivo, icono: HelpCircle };
                    const Icono = info.icono;
                    const pct = datos.visitas_periodo ? Math.round((d.visitas / datos.visitas_periodo) * 100) : 0;
                    return (
                      <li key={d.dispositivo}>
                        <div className="trafico-dispositivo-fila">
                          <span><Icono size={15} aria-hidden="true" /><span>{info.texto} - <strong>{pct}%</strong></span></span>
                          <small>{d.visitas.toLocaleString('es-MX')} visitas · {d.visitantes.toLocaleString('es-MX')} visitantes</small>
                        </div>
                        <div className="trafico-medidor" aria-hidden="true">
                          <div style={{ width: `${pct}%`, background: c.visitas }} />
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          <div className="trafico-ubicaciones">
            <h3><MapPin size={16} aria-hidden="true" /> Principales ubicaciones <small>(aproximadas)</small></h3>
            {!datos.ubicaciones || (datos.ubicaciones.principales.length === 0 && !datos.ubicaciones.sin_ubicacion) ? (
              <p className="text-muted">Sin datos en este periodo.</p>
            ) : (
              <ul className="trafico-dispositivos trafico-ubicaciones-lista">
                {[
                  ...datos.ubicaciones.principales.map(u => ({ clave: `${u.pais}|${u.estado}|${u.ciudad}`, texto: nombreUbicacion(u), visitas: u.visitas, visitantes: u.visitantes })),
                  ...(datos.ubicaciones.otras ? [{ clave: 'otras', texto: `Otras ubicaciones (${datos.ubicaciones.otras.lugares})`, ...datos.ubicaciones.otras, secundaria: true }] : []),
                  ...(datos.ubicaciones.sin_ubicacion ? [{ clave: 'sin', texto: 'Sin ubicación detectada', ...datos.ubicaciones.sin_ubicacion, secundaria: true }] : [])
                ].map(u => {
                  const pct = datos.visitas_periodo ? Math.round((u.visitas / datos.visitas_periodo) * 100) : 0;
                  return (
                    <li key={u.clave}>
                      <div className={`trafico-dispositivo-fila${u.secundaria ? ' trafico-fila-secundaria' : ''}`}>
                        <span><span>{u.texto} - <strong>{pct}%</strong></span></span>
                        <small>{u.visitas.toLocaleString('es-MX')} visitas · {u.visitantes.toLocaleString('es-MX')} visitantes</small>
                      </div>
                      <div className="trafico-medidor" aria-hidden="true">
                        <div style={{ width: `${pct}%`, background: u.secundaria ? c.texto : c.visitas }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <Suspense fallback={<p className="text-muted">Cargando mapa...</p>}>
            <TraficoMapa dias={dias} modoOscuro={modoOscuro} />
          </Suspense>
        </>
      )}
    </section>
  );
};

export default TraficoCard;
